import { DEVICE_GATEWAY_MENU_TITLE_KEY } from 'views/panels/admin-panel/DeviceGateway/deviceGateway.menuI18n'

const SUPER_ADMIN_PATH = '/super-admin/'
const ADMIN_PANEL_SEGMENT = '/admin-panel/'
const API_DESIGNER_PATH = '/admin-panel/api-designer'
const DEVICE_GATEWAY_PATH = '/admin-panel/device-gateway'
const DEFAULT_GROUP_MENU_ID = 'admin-panel'
const SEQ_STEP = 0.01

// NavItem translates a title only when the item has an icon, so the entry
// always carries one: its neighbour's, or this one when the neighbour has none
const FALLBACK_ICON = { icon: 'cpu', iconSource: 'feather', iconType: 'icon' }

const normalizeMenuChildren = (items = []) => {
  if (Array.isArray(items)) return items
  if (items && typeof items === 'object') return Object.values(items)
  return []
}

const normalizeMenuPath = (url = '') =>
  String(url).split('?')[0].replace(/\/$/, '')

const isApiDesignerMenu = (item) => {
  const path = normalizeMenuPath(item?.url)
  return path.startsWith(SUPER_ADMIN_PATH) && path.endsWith(API_DESIGNER_PATH)
}

const isSuperAdminPanelMenu = (item) => {
  const path = normalizeMenuPath(item?.url)
  return path.startsWith(SUPER_ADMIN_PATH) && path.includes(ADMIN_PANEL_SEGMENT)
}

// the neighbour's own prefix, so the entry links the way the items around it do
const deviceGatewayUrlBeside = (item) => {
  const path = normalizeMenuPath(item.url)
  return `${path.slice(
    0,
    path.indexOf(ADMIN_PANEL_SEGMENT),
  )}${DEVICE_GATEWAY_PATH}`
}

const containsDeviceGatewayMenu = (items = []) =>
  normalizeMenuChildren(items).some((item) => {
    const path = normalizeMenuPath(item?.url)
    return (
      path.endsWith(DEVICE_GATEWAY_PATH) ||
      path.includes(`${DEVICE_GATEWAY_PATH}/`) ||
      containsDeviceGatewayMenu(item?.children)
    )
  })

const iconOf = (item) =>
  item.icon
    ? {
        icon: item.icon,
        iconColor: item.iconColor,
        iconSource: item.iconSource,
        iconText: item.iconText,
        iconTextColor: item.iconTextColor,
        iconType: item.iconType,
      }
    : { ...FALLBACK_ICON, iconColor: item.iconColor }

/** The entry, styled and grouped like the menu item it is placed next to. */
const buildDeviceGatewayMenu = ({ access, neighbour, seq, url }) => ({
  ...iconOf(neighbour),
  access,
  color: neighbour.color,
  groupMenuId: neighbour.groupMenuId,
  id: `${neighbour.groupMenuId || DEFAULT_GROUP_MENU_ID}-device-gateway`,
  linkType: 'router',
  seq,
  title: DEVICE_GATEWAY_MENU_TITLE_KEY,
  type: 'item',
  url,
})

/**
 * Directly below API Designer: both screens administer wiring that used to
 * be edited in the database, and the anchor's access list already decides
 * who may see either of them.
 */
const insertAfterApiDesigner = (items) => {
  let inserted = false
  const visit = (menuItems = []) =>
    normalizeMenuChildren(menuItems).reduce((result, item) => {
      if (!item) return result
      result.push(
        item.children ? { ...item, children: visit(item.children) } : item,
      )
      if (!inserted && isApiDesignerMenu(item)) {
        result.push(
          buildDeviceGatewayMenu({
            access: item.access || [],
            neighbour: item,
            seq: Number(item.seq || 0) + SEQ_STEP,
            url: normalizeMenuPath(item.url).replace(
              API_DESIGNER_PATH,
              DEVICE_GATEWAY_PATH,
            ),
          }),
        )
        inserted = true
      }
      return result
    }, [])
  const result = visit(items)
  return inserted ? result : null
}

/**
 * At the end of the group holding the other Super Admin `/admin-panel/...`
 * items, with that group's access list (its last such item's when the group
 * carries none).
 */
const appendToAdminPanelGroup = (items) => {
  let inserted = false
  const visit = (menuItems, container) => {
    const children = normalizeMenuChildren(menuItems)
    const panelItems = children.filter(isSuperAdminPanelMenu)
    if (!inserted && panelItems.length) {
      inserted = true
      const neighbour = panelItems[panelItems.length - 1]
      const groupAccess =
        Array.isArray(container?.access) && container.access.length
          ? container.access
          : neighbour.access || []
      const lastSeq = Math.max(
        ...children.map((child) => Number(child?.seq) || 0),
      )
      return [
        ...children,
        buildDeviceGatewayMenu({
          access: groupAccess,
          neighbour,
          seq: lastSeq + SEQ_STEP,
          url: deviceGatewayUrlBeside(neighbour),
        }),
      ]
    }
    return children.map((item) =>
      item?.children && !inserted
        ? { ...item, children: visit(item.children, item) }
        : item,
    )
  }
  const result = visit(items, null)
  return inserted ? result : null
}

/**
 * Adds the Device Gateway screen to the Super Admin menu in code, for an
 * administrator only: `isAdmin` from useAuth, the rule the screen and core
 * check too. Anyone else gets the menu as it is.
 *
 * The menu is a set of `perspective-menu-collection` rows, so reaching a new
 * screen would otherwise mean a SQL insert on the database that seeds
 * production. Same approach as `addLabOrderFormDesignerMenu`. A menu that
 * already links the screen (or its old HL7 routes path) is left as it is.
 */
export const addDeviceGatewayMenu = (items = [], { isAdmin = false } = {}) => {
  if (isAdmin !== true) return items
  if (!Array.isArray(items) || containsDeviceGatewayMenu(items)) return items
  return (
    insertAfterApiDesigner(items) || appendToAdminPanelGroup(items) || items
  )
}
